import React, { useEffect, useState } from 'react';

import { Button, Card, Chip, Stack, Typography } from '@mui/joy';

import { track } from '../../../services/analytics';
import {
  EVENT_TUTORIAL,
  EVNT_tutorial_screen_main_cta_clicked,
  EVNT_tutorial_screen_secondary_cta_clicked,
  PGV_tutorial_screen_shown,
} from '../../../services/analytics/events';
import { useOnboardingTour } from '../../../services/onboarding';
import {
  useOnboardingDismissalStore,
  useShouldShowOnboarding,
} from '../../../services/onboarding/useOnboardingDismissalStore';
import { useHomeOnboardingSteps } from '../steps/useHomeOnboardingSteps';

const MOUNT_DELAY_MS = 800;
const ONBOARDING_KEY = 'home';
const ALWAYS_SHOW_FOR_TESTING = process.env.NODE_ENV === 'development';

// Launcher screen identity for the tutorial analytics (analytics_sheet.pdf).
// Same tutorial as the tour steps (feature_name "dashboard", shown on the
// dashboard); tutorial_step_number "Launcher" marks the entry screen, and the
// header text matches the sheet's first sample value exactly.
const LAUNCHER_EVENT_PROPS = {
  platform_page: 'dashboard',
  feature_name: 'dashboard',
  tutorial_step_number: 'Launcher',
  tutorial_screen_header_text: 'Your new homepage',
};

export const HomepageOnboardingLauncher: React.FC = () => {
  const steps = useHomeOnboardingSteps();
  const { canShow, start: startTour } = useOnboardingTour(steps);
  const storageAllowsShowing = useShouldShowOnboarding(ONBOARDING_KEY);
  const shouldShowFromStorage = ALWAYS_SHOW_FOR_TESTING || storageAllowsShowing;
  const markShown = useOnboardingDismissalStore((s) => s.markShown);
  const recordRemindLater = useOnboardingDismissalStore((s) => s.recordRemindLater);
  const markCompleted = useOnboardingDismissalStore((s) => s.markCompleted);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!shouldShowFromStorage) return;
    const timer = window.setTimeout(() => {
      if (!ALWAYS_SHOW_FOR_TESTING) markShown(ONBOARDING_KEY);
      setVisible(true);
    }, MOUNT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [shouldShowFromStorage, markShown]);

  // PGV only when the launcher card is actually rendered to the user — gated on
  // the SAME condition as the render below, so we don't log a "screen shown" for
  // a card that returns null (e.g. visible but canShow false).
  useEffect(() => {
    if (canShow && shouldShowFromStorage && visible)
      track(PGV_tutorial_screen_shown, EVENT_TUTORIAL, LAUNCHER_EVENT_PROPS);
  }, [canShow, shouldShowFromStorage, visible]);

  if (!canShow || !shouldShowFromStorage || !visible) return null;

  const start = () => {
    track(EVNT_tutorial_screen_main_cta_clicked, EVENT_TUTORIAL, {
      ...LAUNCHER_EVENT_PROPS,
      button_text: 'Start the 1 min tour',
    });
    setVisible(false);
    if (!ALWAYS_SHOW_FOR_TESTING) markCompleted(ONBOARDING_KEY);
    startTour();
  };
  const remindLater = () => {
    track(EVNT_tutorial_screen_secondary_cta_clicked, EVENT_TUTORIAL, {
      ...LAUNCHER_EVENT_PROPS,
      button_text: 'Remind me later',
    });
    setVisible(false);
    if (!ALWAYS_SHOW_FOR_TESTING) recordRemindLater(ONBOARDING_KEY);
  };

  return (
    <Card
      variant='outlined'
      sx={(t) => ({
        position: 'fixed',
        bottom: 24,
        left: 24,
        width: 427,
        zIndex: 9000,
        p: 3,
        gap: 2,
        padding: '36px 33px 36px 33px',
        borderRadius: '14px',
        boxShadow: 'lg',
        bgcolor: t.palette.background.surface,
        borderColor: t.palette['expert']['light'],
        animation: 'homepageLauncherSlideIn 0.45s cubic-bezier(0.2, 0.8, 0.2, 1)',
        '@keyframes homepageLauncherSlideIn': {
          from: { transform: 'translateY(20px)', opacity: 0 },
          to: { transform: 'translateY(0)', opacity: 1 },
        },
      })}
    >
      <Chip
        color='primary'
        size='lg'
        sx={{
          color: (theme) => theme.palette.primary.solidColor,
          background: (theme) => theme.palette['expert']['light'],
        }}
      >
        New
      </Chip>
      <Stack>
        <Typography level='h3' color='primary' fontWeight={'xl'}>
          Your new homepage, personalized to your plan
        </Typography>

        <Typography level='body-md' sx={{ color: (theme) => theme.palette.text.secondary }}>
          We&rsquo;ve reorganized everything around your plan, so what matters to you is up front.
        </Typography>
      </Stack>

      <Stack direction='row' gap={1}>
        <Button variant='outlined' size='lg' onClick={remindLater}>
          Remind me later
        </Button>
        <Button
          variant='cta'
          sx={{ background: (theme) => theme.palette['expert']['light'] }}
          size='lg'
          onClick={start}
        >
          Start the 1 min tour
        </Button>
      </Stack>
    </Card>
  );
};
